import { c as _c } from "react/compiler-runtime";
/* eslint-disable react/jsx-props-no-spreading */
import type { Placement } from '@popperjs/core';
import type { CSSProperties, ReactNode, RefObject, JSX } from 'react';
import { useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { usePopper } from 'react-popper';
import { useOnClickOutside } from '../../hooks/click-outside';
import { zIndices } from '../../theme';
import { PopoverAnimation } from '../popover-animation';
interface PopperChildProps {
  toggle: (isOpen?: boolean) => void;
}
export interface PopperTriggerProps {
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  triggerRef?: RefObject<any>;
  toggle: (isOpen?: boolean) => void;
}
interface BaseProps {
  children: ReactNode | ((props: PopperChildProps) => ReactNode);
  trigger: (props: PopperTriggerProps) => ReactNode;
  offsetSkidding?: number;
  offsetDistance?: number;
  placement?: Placement;
  portalElement?: HTMLElement;
  style?: CSSProperties;
  testId?: string;
}
type UncontrolledProps = BaseProps;
interface LegacyPopperChildProps {
  toggle: () => void;
}
interface LegacyPopperTriggerProps {
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  triggerRef?: RefObject<any>;
  toggle: () => void;
}
interface LegacyProps extends BaseProps {
  isOpen?: boolean;
  onToggle?: () => void;
  trigger: (props: LegacyPopperTriggerProps) => ReactNode;
  children: ReactNode | ((props: LegacyPopperChildProps) => ReactNode);
}
interface ControlledProps extends BaseProps {
  isOpen?: boolean;
  onToggle: (isOpen?: boolean) => void;
}
export function ControlledPopper({
  isOpen,
  onToggle,
  children,
  trigger,
  offsetSkidding,
  offsetDistance = 8,
  placement = 'bottom-end',
  portalElement,
  style = {},
  testId
}: ControlledProps): JSX.Element {
  const [popoverRef, setPopoverRef] = useState<HTMLElement | null>(null);
  const triggerRef = useRef<HTMLElement>(null);
  const {
    attributes,
    update,
    styles
  } = usePopper(triggerRef.current, popoverRef, {
    placement,
    modifiers: [{
      name: 'offset',
      options: {
        offset: [offsetSkidding, offsetDistance]
      }
    }]
  });
  const toggle = (newValue?: boolean): void => {
    if (typeof newValue === 'boolean') {
      onToggle(newValue);
    } else {
      onToggle(!isOpen);
    }
  };
  useEffect(() => {
    if (update) {
      // eslint-disable-next-line @typescript-eslint/no-floating-promises
      update();
    }
  }, [isOpen, update]);
  useOnClickOutside(popoverRef, e => {
    if (isOpen && e.target !== triggerRef.current) {
      toggle(false);
    }
  });
  const popoverContent = <div {...attributes.popper} data-testid={testId} style={{
    ...styles.popper,
    width: style.width,
    zIndex: zIndices.popover
  }} ref={setPopoverRef}>
      <PopoverAnimation direction="down" isOpen={isOpen ?? false}>
        <div className="bg-background shadow-overlayShadow rounded-xs border" style={style}>
          {typeof children === 'function' ? children({
          toggle
        }) : children}
        </div>
      </PopoverAnimation>
    </div>;
  return <>
      {trigger?.({
      toggle,
      triggerRef
    })}
      {portalElement ? createPortal(popoverContent, portalElement) : popoverContent}
    </>;
}
export function UncontrolledPopper(props) {
  const $ = _c(8);
  let children;
  let rest;
  if ($[0] !== props) {
    ({
      children,
      ...rest
    } = props);
    $[0] = props;
    $[1] = children;
    $[2] = rest;
  } else {
    children = $[1];
    rest = $[2];
  }
  const [isOpen, setIsOpen] = useState(false);
  let t0;
  if ($[3] === Symbol.for("react.memo_cache_sentinel")) {
    t0 = newValue => setIsOpen(newValue ?? false);
    $[3] = t0;
  } else {
    t0 = $[3];
  }
  let t1;
  if ($[4] !== children || $[5] !== isOpen || $[6] !== rest) {
    t1 = <ControlledPopper {...rest} isOpen={isOpen} onToggle={t0}>{children}</ControlledPopper>;
    $[4] = children;
    $[5] = isOpen;
    $[6] = rest;
    $[7] = t1;
  } else {
    t1 = $[7];
  }
  return t1;
}

/**
 * @deprecated Please use ControlledPopper or UncontrolledPopper instead
 */
export function Popper(props) {
  const $ = _c(16);
  let children;
  let isOpen;
  let onToggle;
  let rest;
  let testId;
  if ($[0] !== props) {
    ({
      onToggle,
      isOpen,
      children,
      testId,
      ...rest
    } = props);
    $[0] = props;
    $[1] = children;
    $[2] = isOpen;
    $[3] = onToggle;
    $[4] = rest;
    $[5] = testId;
  } else {
    children = $[1];
    isOpen = $[2];
    onToggle = $[3];
    rest = $[4];
    testId = $[5];
  }
  const isControlled = typeof isOpen !== "undefined";
  if (isControlled) {
    const toggle = typeof onToggle === "function" ? onToggle : _temp;
    let t0;
    if ($[6] !== children || $[7] !== isOpen || $[8] !== rest || $[9] !== testId || $[10] !== toggle) {
      t0 = <ControlledPopper {...rest} onToggle={toggle} isOpen={isOpen} testId={testId}>{children}</ControlledPopper>;
      $[6] = children;
      $[7] = isOpen;
      $[8] = rest;
      $[9] = testId;
      $[10] = toggle;
      $[11] = t0;
    } else {
      t0 = $[11];
    }
    return t0;
  }
  let t0;
  if ($[12] !== children || $[13] !== rest || $[14] !== testId) {
    t0 = <UncontrolledPopper {...rest} testId={testId}>{children}</UncontrolledPopper>;
    $[12] = children;
    $[13] = rest;
    $[14] = testId;
    $[15] = t0;
  } else {
    t0 = $[15];
  }
  return t0;
}
function _temp() {}